import { useCallback, useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconClose } from "@icons/index";
import { useKeyup } from "@hooks/utils";
import { CommonButton } from "@components/CommonButton";
import Section from "@components/Section";
import type { UserAssetRecord } from "@apis/http/api";
import "./index.scss";
import { IconVideoEmpty } from "@icons/common";
import { CardListSidebar } from "./CardListSidebar";
import { DetailGeneratingView } from "./DetailGeneratingView";
import { DetailVideoPlayer } from "./DetailVideoPlayer";
import { getAspectRatio, getVideoUrl } from "./helpers";
import { useJobDetail } from "./useJobDetail";
import { VideoInfoPanel } from "./VideoInfoPanel";

interface VideoDetailModalProps {
  open: boolean;
  records: UserAssetRecord[];
  initialIndex?: number;
  onClose: () => void;
}

const GlobalVideoDetailModal = ({ open, records, initialIndex = 0, onClose }: VideoDetailModalProps) => {
  const [activeJobId, setActiveJobId] = useState<number | undefined>(records[initialIndex]?.jobItem?.jobId);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const recordsRef = useRef(records);

  recordsRef.current = records;

  useEffect(() => {
    setActiveJobId(recordsRef.current[initialIndex]?.jobItem?.jobId);
    setPostModalOpen(false);
  }, [initialIndex, open]);

  const activeRecord = records.find((r) => r.jobItem?.jobId === activeJobId) ?? records[0];
  const jobId = activeRecord?.jobItem?.jobId;
  const activeStatus = activeRecord?.jobItem?.status;
  const { detail: jobDetail, loading: detailLoading } = useJobDetail(open ? jobId : undefined, activeStatus);

  const handleDeleted = useCallback(() => {
    if (records.length <= 1) {
      onClose();
      return;
    }
    const activeIdx = records.findIndex((r) => r.jobItem?.jobId === activeJobId);
    const remaining = records.filter((r) => r.jobItem?.jobId !== activeJobId);
    // 删除最后一项时选上一个，否则选下一个（删除后同位置元素）
    const nextRecord = activeIdx >= remaining.length ? remaining[remaining.length - 1] : remaining[activeIdx];
    setActiveJobId(nextRecord?.jobItem?.jobId);
  }, [activeJobId, records, onClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "overlay";
    }

    return () => {
      document.body.style.overflow = "overlay";
    };
  }, [open]);

  useKeyup(() => {
    if (open) onClose();
  });

  if (!open || !activeRecord) return null;

  const isActiveProcessing = activeStatus === "processing" || activeStatus === "queued";
  const videoUrl = getVideoUrl(jobDetail, activeRecord);
  const aspectRatio = getAspectRatio(jobDetail, activeRecord);

  const renderMainContent = () => {
    if (isActiveProcessing) {
      return <DetailGeneratingView aspectRatio={aspectRatio} record={activeRecord} />;
    }
    if (videoUrl) {
      return (
        <DetailVideoPlayer key={videoUrl} videoURL={videoUrl} aspectRatio={aspectRatio} suspended={postModalOpen} />
      );
    }
    return (
      <div className="flex flex-col items-center justify-center text-kds-fg-subtle max-w-[400px] text-center">
        <IconVideoEmpty className="w-[100px] h-[100px]" />
        <span className="text-[16px] font-medium text-kds-fg mt-[8px]">
          <Trans>Couldn't generate video</Trans>
        </span>
        {jobDetail?.errorMessage && (
          <span className="text-[14px] text-kds-fg-soft mt-[4px]">{t(jobDetail.errorMessage)}</span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-1000 bg-kds-bg-soft overflow-hidden flex">
      <div className="inline-block absolute top-[12px] left-[32px]!">
        <CommonButton className="w-[32px] h-[32px] p-0!" onClick={onClose}>
          <IconClose className="w-[24px]! h-[24px]!" />
        </CommonButton>
      </div>

      <Section
        status={!isActiveProcessing && detailLoading && !videoUrl ? "loading" : "succeeded"}
        className="flex-3 min-w-0 flex items-center justify-center bg-kds-bg-soft p-[32px]"
      >
        {renderMainContent()}
      </Section>
      <div className="flex-2 min-w-0 overflow-hidden" style={{ boxShadow: "-4px 0 10px 0 var(--kds-shadow-soft)" }}>
        <VideoInfoPanel
          onClose={onClose}
          record={activeRecord}
          jobDetail={jobDetail}
          detailLoading={detailLoading}
          onDeleted={handleDeleted}
          onPostModalOpenChange={setPostModalOpen}
        />
      </div>
      <div className="w-[110px] shrink-0 border-l border-kds-border overflow-hidden">
        <CardListSidebar records={records} activeJobId={activeJobId} onSelect={setActiveJobId} />
      </div>
    </div>
  );
};

export default GlobalVideoDetailModal;
